<script setup>
import { computed } from 'vue'

const props = defineProps({
    driver: {
        type: Object,
        required: true,
    },
})

const canadaHazmat = computed(() => {
    return props.driver?.driver?.expiration?.canada_hazmat || 'N/A'
})

const city = computed(() => {
    return props.driver?.personal?.address?.city || 'N/A'
})

const license = computed(() => {
    return props.driver?.driver?.expiration?.license || 'N/A'
})

const medical = computed(() => {
    return props.driver?.driver?.expiration?.medical || 'N/A'
})

const passport = computed(() => {
    return props.driver?.driver?.expiration?.passport || 'N/A'
})

const state = computed(() => {
    return props.driver?.personal?.address?.state || 'N/A'
})

const street1 = computed(() => {
    return props.driver?.personal?.address?.street1 || 'N/A'
})

const street2 = computed(() => {
    return props.driver?.personal?.address?.street2 || 'N/A'
})

const usaHazmat = computed(() => {
    return props.driver?.driver?.expiration?.usa_hazmat || 'N/A'
})

const visa = computed(() => {
    return props.driver?.driver?.expiration?.visa || 'N/A'
})

const wic = computed(() => {
    return props.driver?.driver?.expiration?.wic || 'N/A'
})

const zip = computed(() => {
    return props.driver?.personal?.address?.postal_code || 'N/A'
})
</script>

<template>
    <div class="flex flex-col mt-6 w-full overflow-auto">
        <div class="flex flex-col px-6 mb-6 w-full">
            <h2 class="text-gray-500 font-semibold mb-3">Work information</h2>
            <div class="flex flex-nowrap border border-gray-300 w-full">
                <div
                    class="flex flex-col border-r border-r-gray-300 p-2 h-full w-full"
                >
                    <span class="text-sm mb-2 text-gray-500"> Mobile </span>
                    <div class="flex items-center mb-2">
                        <a
                            class="text-sm font-semibold mr-2 underline"
                            :href="`tel:${driver.work.mobile_phone}`"
                        >
                            {{ driver.work.mobile_phone }}
                        </a>
                    </div>
                </div>

                <div class="flex flex-col p-2 h-full w-full">
                    <span class="text-sm mb-2 text-gray-500"> Email </span>

                    <div class="flex items-center mb-2">
                        <a
                            class="text-sm font-semibold underline"
                            :href="`mailto:${driver.work.email}`"
                        >
                            {{ driver.work.email }}
                        </a>
                    </div>
                </div>
            </div>
        </div>
        <div class="flex flex-col px-6 w-full mb-6">
            <h2 class="text-gray-500 font-semibold mb-3">Driver Expirations</h2>
            <div class="flex flex-nowrap border border-gray-300 w-full mb-3">
                <div
                    class="flex flex-col border-r border-r-gray-300 p-2 h-full w-full"
                >
                    <span class="text-sm mb-2 text-gray-500"> License </span>
                    <div class="flex items-center mb-2">
                        <span class="text-sm font-semibold mr-2">
                            {{ license }}
                        </span>
                    </div>
                </div>
                <div
                    class="flex flex-col border-r border-r-gray-300 p-2 h-full w-full"
                >
                    <span class="text-sm mb-2 text-gray-500"> WIC </span>
                    <div class="flex items-center mb-2">
                        <span class="text-sm font-semibold mr-2">
                            {{ wic }}
                        </span>
                    </div>
                </div>
                <div class="flex flex-col p-2 h-full w-full">
                    <span class="text-sm mb-2 text-gray-500"> USA Hazmat </span>

                    <div class="flex items-center mb-2">
                        <span class="text-sm font-semibold">
                            {{ usaHazmat }}
                        </span>
                    </div>
                </div>
            </div>
            <div class="flex flex-nowrap border border-gray-300 w-full mb-3">
                <div
                    class="flex flex-col border-r border-r-gray-300 p-2 h-full w-full"
                >
                    <span class="text-sm mb-2 text-gray-500">
                        Canada Hazmat
                    </span>
                    <div class="flex items-center mb-2">
                        <span class="text-sm font-semibold mr-2">
                            {{ canadaHazmat }}
                        </span>
                    </div>
                </div>
                <div
                    class="flex flex-col border-r border-r-gray-300 p-2 h-full w-full"
                >
                    <span class="text-sm mb-2 text-gray-500"> Visa </span>
                    <div class="flex items-center mb-2">
                        <span class="text-sm font-semibold mr-2">
                            {{ visa }}
                        </span>
                    </div>
                </div>
                <div class="flex flex-col p-2 h-full w-full">
                    <span class="text-sm mb-2 text-gray-500"> Passport </span>

                    <div class="flex items-center mb-2">
                        <span class="text-sm font-semibold">
                            {{ passport }}
                        </span>
                    </div>
                </div>
            </div>
            <div class="flex flex-nowrap border border-gray-300 w-full">
                <div class="flex flex-col p-2 h-full w-full">
                    <span class="text-sm mb-2 text-gray-500"> Medical </span>
                    <div class="flex items-center mb-2">
                        <span class="text-sm font-semibold mr-2">
                            {{ medical }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <div class="flex flex-col px-6 w-full mb-6">
            <h2 class="text-gray-500 font-semibold mb-3">
                Personal Information
            </h2>
            <div class="flex flex-nowrap border border-gray-300 w-full mb-3">
                <div
                    class="flex flex-col border-r border-r-gray-300 p-2 h-full w-full"
                >
                    <span class="text-sm mb-2 text-gray-500"> Mobile </span>
                    <div class="flex items-center mb-2">
                        <a
                            class="text-sm font-semibold"
                            :href="`tel:${driver.personal.mobile_phone}`"
                        >
                            {{ driver.personal.mobile_phone || 'N/A' }}
                        </a>
                    </div>
                </div>
                <div class="flex flex-col p-2 h-full w-full">
                    <span class="text-sm mb-2 text-gray-500"> Email </span>
                    <div class="flex items-center mb-2">
                        <a
                            class="text-sm font-semibold"
                            :href="`mailto:${driver.personal.email}`"
                        >
                            {{ driver.personal.email || 'N/A' }}
                        </a>
                    </div>
                </div>
            </div>
        </div>
        <div class="flex flex-col px-6 w-full mb-6">
            <h2 class="text-gray-500 font-semibold mb-3">Address</h2>
            <div class="flex flex-nowrap border border-gray-300 w-full mb-3">
                <div
                    class="flex flex-col border-r border-r-gray-300 p-2 h-full w-full"
                >
                    <span class="text-sm mb-2 text-gray-500"> Street </span>
                    <div class="flex items-center mb-2">
                        <span class="text-sm font-semibold mr-2">
                            {{ street1 }}
                        </span>
                    </div>
                </div>
                <div
                    class="flex flex-col border-r border-r-gray-300 p-2 h-full w-full"
                >
                    <span class="text-sm mb-2 text-gray-500"> Street 2 </span>
                    <div class="flex items-center mb-2">
                        <span class="text-sm font-semibold mr-2">
                            {{ street2 }}
                        </span>
                    </div>
                </div>
                <div class="flex flex-col p-2 h-full w-full">
                    <span class="text-sm mb-2 text-gray-500"> City </span>

                    <div class="flex items-center mb-2">
                        <span class="text-sm font-semibold">
                            {{ city }}
                        </span>
                    </div>
                </div>
            </div>
            <div class="flex flex-nowrap border border-gray-300 w-full mb-3">
                <div
                    class="flex flex-col border-r border-r-gray-300 p-2 h-full w-full"
                >
                    <span class="text-sm mb-2 text-gray-500"> State </span>
                    <div class="flex items-center mb-2">
                        <span class="text-sm font-semibold mr-2">
                            {{ state }}
                        </span>
                    </div>
                </div>
                <div class="flex flex-col p-2 h-full w-full">
                    <span class="text-sm mb-2 text-gray-500"> Zip </span>
                    <div class="flex items-center mb-2">
                        <span class="text-sm font-semibold mr-2">
                            {{ zip }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <div class="flex flex-col px-6 w-full">
            <h2 class="text-gray-500 font-semibold mb-3">Emergency Contact</h2>
            <div class="flex flex-nowrap border border-gray-300 w-full mb-3">
                <div
                    class="flex flex-col border-r border-r-gray-300 p-2 h-full w-full"
                >
                    <span class="text-sm mb-2 text-gray-500"> Name </span>
                    <div class="flex items-center mb-2">
                        <span class="text-sm font-semibold">
                            {{
                                driver.personal.emergency_contact.name || 'N/A'
                            }}
                        </span>
                    </div>
                </div>
                <div class="flex flex-col p-2 h-full w-full">
                    <span class="text-sm mb-2 text-gray-500"> Phone </span>
                    <div class="flex items-center mb-2">
                        <a
                            class="text-sm font-semibold"
                            :href="`tel:${driver.personal.emergency_contact.phone}`"
                        >
                            {{ driver.personal.emergency_contact.phone }}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
